import { useState, useEffect, useRef, ChangeEvent, SyntheticEvent, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu } from 'primereact/menu'
import { MenuItem } from 'primereact/menuitem'
import { InputText } from 'primereact/inputtext'

import { useStructureContext } from '../contexts/StructureContext'
import { MIN_SEARCH_LENGTH, useSearchContext } from '../contexts/SearchContext'
import { useKeyboard } from '../../hooks/keyboard'
import { Column, Row } from './pure/FlexContainer'
import NavigationLink from '../side-bar/NavigationLink'
import SearchResult from '../../models/SearchResult'
import { CalendarActionWrapper } from './CalendarButton'
import PrimeIcon from './pure/PrimeIcon'
import CustomIcon from './pure/CustomIcon'
import TooltipWrapper from './pure/TooltipWrapper'
import { Ancestor } from '../../models/ApiStructureModel'
import ClassNameBuilder from '../../tools/ClassNameBuilder'
import { capture } from '../../tools/dom/event'
import { formatDate } from '../../tools/format'
import t, { _t, _ta } from '../../tools/t'
import MenuBuilder from '../../tools/MenuBuilder'

/**
 * Global search bar with results popup.
 * Directly coupled with the SearchContext.
 * NOTE: This component makes heavy use of refs only because it is convenient.
 */
export default function GlobalSearch() {
    const navigate = useNavigate()
    const {
        debouncedSearch,
        resetSearch,
        typeFilters,
        searchResults,
        recentSearches,
        storeRecentSearch,
        deleteRecentSearch,
        loading,
        error,
    } = useSearchContext()
    const { structure } = useStructureContext()
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilters, setSelectedFilters] = useState<string[]>([])
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [dateRange, setDateRange] = useState<Date[]>([])
    const [showFilter, setShowFilter] = useState(false)
    const menuRef = useRef<Menu>(null)
    const keyboardRef = useRef<HTMLDivElement>(null)
    const menu = menuRef.current
    const inputRef = useRef<HTMLInputElement>(null)
    const input = inputRef.current

    const canPerformSearch = searchTerm.length >= MIN_SEARCH_LENGTH
    const hasResults = searchResults.length > 0 && searchTerm.length > 0 && !loading
    let searchItems = hasResults ? searchResults : recentSearches
    if (canPerformSearch && !hasResults) searchItems = []

    const onSearchTermChange = (event: ChangeEvent<HTMLInputElement>) =>
        setSearchTerm(event.target.value)

    const toggleFilter = (filter: string) => {
        if (selectedFilters.includes(filter))
            setSelectedFilters(selectedFilters.filter((f) => f !== filter))
        else setSelectedFilters([...selectedFilters, filter])
    }

    const openSearch = (event: SyntheticEvent) => {
        menu?.show(event)
        // Due to a race condition with the menu opening,
        //  claiming focus here has no practical effect.
        input?.focus()
    }

    const closeSearch = () => {
        // Always blur input before hiding menu, or the menu will reappear!
        input?.blur()
        // The menu requires an event to hide, but it doesn't actually need one.
        menu?.hide({} as SyntheticEvent)
        setSelectedIndex(0)
        resetSearch()
        setSearchTerm('')
    }

    const toggleHeader = (event: SyntheticEvent) => {
        // Avoid interfering with the menu's open state.
        event.preventDefault()
        event.stopPropagation()
        setShowFilter(!showFilter)
    }

    const moveIndex = (dir: 1 | -1) => {
        const i = selectedIndex + dir
        const n = searchItems.length - 1
        const clamped = Math.max(Math.min(i, n), 0)
        setSelectedIndex(clamped)
    }

    const selectIndex = () => {
        const result = searchItems[selectedIndex]
        if (!result) return
        navigate(result.searchLink)
        closeSearch()
        storeRecentSearch(result)
    }

    const clickItem = (result: SearchResult) => {
        storeRecentSearch(result)
        closeSearch()
        navigate(result.searchLink)
    }

    useEffect(() => {
        if (searchTerm.length <= 0) resetSearch()
        if (searchTerm.length < MIN_SEARCH_LENGTH) return
        const filters = showFilter ? selectedFilters : []
        debouncedSearch(searchTerm, filters, [])
    }, [searchTerm, selectedFilters, showFilter])

    // NOTE: Using the wrapping <div> as keyboard target to avoid leaking keyboard events outside the search bar.
    const keyboardTarget = keyboardRef.current
    useKeyboard('Escape', closeSearch, [input, menu], keyboardTarget)
    useKeyboard('ArrowDown', () => moveIndex(1), [moveIndex], keyboardTarget)
    useKeyboard('ArrowUp', () => moveIndex(-1), [moveIndex], keyboardTarget)
    useKeyboard('Enter', selectIndex, [selectIndex], keyboardTarget)

    const status = (() => {
        if (error) return t('search_error_results')
        if (loading) return t('search_loading')
        if (canPerformSearch && !hasResults) return t('search_no_results')
        if (hasResults) return t('search_results')
        else if (recentSearches.length) return t('search_recent_results')
        return t('search_status_fallback')
    })()

    const menuItems = new MenuBuilder()
    // If the filter is show, add it on top of the search UI.
    menuItems.addIf(
        {
            template: (
                <SearchResultHeader
                    dateRange={dateRange}
                    onDateRangeChange={(range) => setDateRange(range)}
                    onFilterClick={toggleFilter}
                    typeFilters={typeFilters}
                    selectedFilters={selectedFilters}
                />
            ),
        },
        showFilter
    )
    // Always show search status.
    menuItems.add({ template: <SearchStatusLabel label={status} /> })
    // Add search results to menu model.
    if (hasResults)
        searchItems.forEach((result, i) =>
            menuItems.add({
                template: () => (
                    <SearchResultItem
                        result={result}
                        ancestors={structure.findEntryAncestors(result.ancestorId)}
                        selected={i === selectedIndex}
                        onClick={() => clickItem(result)}
                        last={i === searchItems.length - 1}
                    />
                ),
            })
        )
    else
        searchItems.forEach((result, i) =>
            menuItems.add({
                template: () => (
                    <RecentSearchResultItem
                        result={result}
                        selected={i === selectedIndex}
                        onClick={() => clickItem(result)}
                        onDelete={!hasResults ? () => deleteRecentSearch(result) : undefined}
                    />
                ),
            })
        )
    // Always show the search footer.
    menuItems.add({ template: <SearchResultFooter /> })

    return (
        <div className="search-bar" ref={keyboardRef}>
            <PrimeIcon name="search" className="search-bar__icon" />
            <InputText
                ref={inputRef}
                className="search-bar__input"
                placeholder={t('search_field_placeholder')}
                value={searchTerm}
                onChange={onSearchTermChange}
                onClick={openSearch}
            />

            <Row
                className="search-bar__filter-toggle"
                alignment="center"
                arrangement="center"
                onClick={toggleHeader}
            >
                <PrimeIcon name="filter" size={8} />
            </Row>
            <Menu
                ref={menuRef}
                className="search-results"
                popup
                model={menuItems.toArray()}
                // Disable default close-on-escape feature because we handle this manually.
                closeOnEscape={false}
                // When clicking inside the menu, the popup will close according to default behavior.
                // This is undesired behavior, so we ensure the menu doesn't close before we want it to.
                onHide={(e) => input === document.activeElement && menu?.show(e)}
                // Menu claims focus when it opens, so we have to give that focus back to the input.
                onFocus={() => input?.focus()}
            />
        </div>
    )
}

const searchResultIcon = (domainType: string): ReactNode => {
    switch (domainType) {
        case 'user':
            return <PrimeIcon name="user" />
        case 'bolt':
            return <CustomIcon name="bolt" />
        case 'boltConnection':
            return <PrimeIcon name="share-alt" />
        case 'project':
            return <PrimeIcon name="folder" />
    }
}

interface SearchResultItemProps {
    result: SearchResult
    selected: boolean
    last: boolean
    ancestors: Ancestor[]
    onClick: () => void
}
const SearchResultItem = ({
    result,
    selected,
    last,
    ancestors,
    onClick,
}: SearchResultItemProps) => {
    const ancestorNames = ancestors.map(({ name }) => name)
    const firstAncestorName = ancestorNames[0]
    const lastAncestorName = ancestorNames.at(-1)
    const shouldUseLastAncestorName = ancestorNames.length > 1 && lastAncestorName
    const shouldUseEllipsis = ancestorNames.length > 2
    let seperator = ''
    if (ancestors.length > 0) seperator = shouldUseEllipsis ? ' / ... / ' : ' / '

    return (
        <>
            <NavigationLink className="search-result-item" onClick={onClick} selected={selected}>
                <Row arrangement="start" alignment="center" gap={1} wide>
                    <div className="search-result-item__icon">
                        {searchResultIcon(result.domainType)}
                    </div>
                    <Column>
                        <TooltipWrapper label={ancestorNames.join(' / ')} position="bottom">
                            <div className="search-result-item__title">{result.name}</div>
                            <Row className="search-result-item__breadcrumbs">
                                {firstAncestorName}
                                {seperator}
                                {shouldUseLastAncestorName && lastAncestorName}
                            </Row>
                        </TooltipWrapper>
                    </Column>
                </Row>
            </NavigationLink>
            {/* The separator is positioned outside the navigation link wrapper for more elegant alignment styling. */}
            {!last && <div className="search-result-item__separator" />}
        </>
    )
}

interface RecentSearchResultItemProps {
    result: SearchResult
    selected: boolean
    onClick: () => void
    onDelete?: () => void
}
const RecentSearchResultItem = ({
    result,
    selected,
    onClick,
    onDelete,
}: RecentSearchResultItemProps) => (
    <NavigationLink
        onClick={onClick}
        selected={selected}
        className="search-result-item search-result-item--recent"
    >
        <Row alignment="center" arrangement="spaced" wide>
            {result.name}
            {onDelete && (
                <div className="search-result-item__delete" onClickCapture={capture(onDelete)}>
                    <PrimeIcon name="times" />
                </div>
            )}
        </Row>
    </NavigationLink>
)

interface SearchStatusLabelProps {
    label: string
}
const SearchStatusLabel = ({ label }: SearchStatusLabelProps) => (
    <div className="search-results__status">{label}</div>
)

interface SearchResultHeaderProps {
    typeFilters: string[]
    selectedFilters: string[]
    onFilterClick: (filter: string) => void
    dateRange: Date[]
    onDateRangeChange: (range: Date[]) => void
}
const SearchResultHeader = ({
    typeFilters,
    selectedFilters,
    onFilterClick,
    dateRange,
    onDateRangeChange,
}: SearchResultHeaderProps) => (
    <div className="search-results__header search-filter">
        <div className="search-filter__title">{t('search_filter_title')}</div>
        <Row className="search-filter__filters" gap={0.5}>
            {typeFilters.map((f) => (
                <SearchFilterTypeItem
                    key={f}
                    filter={f}
                    onClick={() => onFilterClick(f)}
                    isSelected={selectedFilters.includes(f)}
                />
            ))}
            <SearchFilterDateItem value={dateRange} onRangeChange={onDateRangeChange} />
        </Row>
    </div>
)

interface SearchFilterItemProps {
    content: ReactNode
    isSelected: boolean
    onClick: (event: SyntheticEvent) => void
}
const SearchFilterItem = ({ content, isSelected, onClick }: SearchFilterItemProps) => {
    const classNames = new ClassNameBuilder()
        .add('search-filter__item')
        .addIf('search-filter__item--selected', isSelected)
    return (
        <Row className={classNames.toString()} gap={0.25} alignment="center" onClick={onClick}>
            {content}
            {isSelected && <PrimeIcon className="search-filter__delete" name="times" size={12} />}
        </Row>
    )
}

interface SearchFilterTypeItemProps {
    isSelected: boolean
    filter: string
    onClick: (filter: string) => void
}
const SearchFilterTypeItem = ({ filter, isSelected, onClick }: SearchFilterTypeItemProps) => {
    return (
        <SearchFilterItem
            content={_t('search_filter_' + filter)}
            onClick={() => onClick(filter)}
            isSelected={isSelected}
        />
    )
}

interface SearchFilterDateItemProps {
    value: Date[]
    onRangeChange: (range: Date[]) => void
}
const SearchFilterDateItem = ({ value, onRangeChange }: SearchFilterDateItemProps) => {
    const [open, setOpen] = useState(false)

    const isSelected = !!value.length
    // The value always has a length of 2.
    // Only when the second value has been set can we be sure that the range is complete.
    const hasFullRange = !!value[1]

    const onClick = () => {
        if (isSelected) onRangeChange([])
        else setOpen(!open)
    }

    const onChange = (value: Date[]) => {
        onRangeChange(value)
        const endDate = value[1]
        if (endDate) setOpen(false)
    }

    const content =
        isSelected && hasFullRange
            ? formatDate(value[0]) + ' — ' + formatDate(value[1])
            : t('search_filter_date')

    return (
        <CalendarActionWrapper
            open={open}
            value={value}
            onRangeChange={onChange}
            onHide={() => setOpen(false)}
        >
            <SearchFilterItem onClick={onClick} content={content} isSelected={isSelected} />
        </CalendarActionWrapper>
    )
}

const SearchResultFooter = () => (
    <div className="search-results__footer">{t('search_keyboard_hint')}</div>
)
